import { AnalyticsEvent, AnalyticsManager } from '@oy/analytics';
import { toast } from '@oy/uidesktop';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslate } from 'src/hooks';
import { getAuth } from 'src/redux/selectors';
import {
  FEATURE_SUGGESTION_MIN_TEXT_LENGTH,
  initialErrorState,
} from './constants';

export const useFeatureSuggestionModal = (onClose: () => void) => {
  const { getTranslation } = useTranslate();

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [text, setTextValue] = useState('');
  const [errors, setErrors] = useState(initialErrorState);

  const auth = useSelector(getAuth);

  useEffect(() => {
    if (auth.isSessionExpired && !auth.isShowOtpModal) {
      closeModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isSessionExpired, auth.isShowOtpModal]);

  const onSelectCategory = (val: string) => {
    const exists = selectedCategories.find(v => v === val);
    let categories = selectedCategories;
    if (!exists) {
      categories = [...categories, val];
    } else {
      categories = categories.filter(cat => cat !== val);
    }
    setSelectedCategories(categories);
    setErrors({ ...errors, categories: '' });
  };

  const setText = useCallback(
    (value: string) => {
      setTextValue(value);
      if (!selectedCategories.length) {
        setErrors({
          ...errors,
          categories: getTranslation('feedbackForm_category_required'),
        });
      }
    },
    [errors, getTranslation, selectedCategories.length],
  );

  const closeModal = useCallback(() => {
    onClose();
    setSelectedCategories([]);
    setText('');
    setErrors(initialErrorState);

    AnalyticsManager.instance().track(AnalyticsEvent.click_exit_suggest_form);
  }, [onClose, setText]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const newErrors = { ...initialErrorState };
    if (selectedCategories.length === 0) {
      newErrors.categories = getTranslation('feedbackForm_category_required');
    }
    if (text.length >= 0 && text.length < FEATURE_SUGGESTION_MIN_TEXT_LENGTH) {
      newErrors.text = getTranslation('feedbackForm_tellUsMore_errorMessage');
    }
    setErrors(newErrors);
    if (newErrors.categories || newErrors.text) {
      return;
    }
    const payload = {
      suggest_categories: selectedCategories,
      suggest_open_ended: text,
    };
    AnalyticsManager.instance().track(
      AnalyticsEvent.submit_suggest_form,
      payload,
    );

    toast.success(getTranslation('suggestFeature_submit_successMessage'));
    closeModal();
  };

  const state = {
    selectedCategories,
    text,
    errors,
  };

  return {
    state,
    onSelectCategory,
    onSubmit,
    closeModal,
    setText,
    setErrors,
  };
};
